import "./index.scss";

import { Box, Modal, Stack } from "@mui/material";
import { ReactComponent as DeleteIcon } from "assets/images/square-buttons/close-small.svg";
import { ReactComponent as IconYMCATCALogo } from "assets/images/tca/ymca-tca-logo.svg";
import Button from "components/Button";
import ConfirmationBox from "components/ConfirmationBox";
import ScreenLoader from "components/ScreenLoader";
import Tabs from "components/Tabs";
import {
  DATE_FORMAT,
  DOCUMENT_BEING_PROCESSED,
  DOCUMENT_BEING_SENT,
  DOCUMENT_SENT_FAILED,
  DOCUMENT_SENT_SUCCESSFUL,
  TCA_REMS_ATD_THRESHOLD,
  TCA_REMS_SM_THRESHOLD,
  TCA_REMS_SS_THRESHOLD,
  WorkflowStates,
} from "constants/index";
import { logMessage, scrollToTop } from "helpers/utils";
import html2pdf from "html2pdf.js";
import { TTCAFollowupSteps } from "models/tca";
import {
  addMemberTCAGoals,
  addMemberTCAPersonalFeelingsInventory,
  deleteTCAMemberAppointments,
  emailContract,
  getMemberTCAGoals,
  getMemberTCAPersonalFeelingsInventory,
  getMemberTCARemsScores,
} from "modules/member/memberServices";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-toastify";

import { validateEmail } from "../../../modules/api/emailValidation";
import PFI from "../components/PFI";
import PGP from "../components/PGP";
import SMS from "../components/SMS";
import TCAContract from "../components/TCAContract";
import TCAUserSummary from "../components/TCAUserSummary";

const FollowUpTCASteps = (props: { open: boolean; onClose: any; member: any }) => {
  const methods = useForm<TTCAFollowupSteps>();
  const { getValues, watch, trigger, setValue, reset } = methods;
  const { open, onClose, member } = props;
  const [loading, setLoading] = useState(false);
  const [tabs, setTabs] = useState([]);
  const [tabValue, setTabValue] = useState("pgp");
  const [currentPGPView, setCurrentPGPView] = useState<"form" | "graph">("form");
  const [currentPFIView, setCurrentPFIView] = useState<"form" | "graph">("form");
  const [currentContractView, setCurrentContractView] = useState<
    "preparation" | "preview" | "certificate"
  >("preparation");
  const [showDataWillBeLostModal, setShowDataWillBeLostModal] = useState(false);
  const [submitButtonLabel, setSubmitButtonLabel] = useState("Next");
  const [previousGoals, setPreviousGoals] = useState<{ [key: string]: string | number }[]>([]);
  const [previousPFIs, setPreviousPFIs] = useState<{ [key: string]: string | number }[]>([]);
  const [userIsSMSubscale, setUserIsSMSubscale] = useState<boolean>();
  const [userIsSSSubscale, setUserIsSSSubscale] = useState<boolean>();
  const [userIsATDSubscale, setUserIsATDSubscale] = useState<boolean>();
  const pgpRef = useRef(null);
  const pfiRef = useRef(null);

  const [showConfirmEmail, setShowConfirmEmail] = useState(false);
  const [contractEmail, setContractEmail] = useState("");
  const [emailSubject, setEmailSubject] = useState("Activelinxx - App Document");

  const allFollowUpTabs = [
    { label: "Personal Goal Profile", state: WorkflowStates.active, value: "pgp" },
    { label: "Personal Feelings Inventory", state: WorkflowStates.locked, value: "pfi" },
    { label: "Self Management Skills", state: WorkflowStates.locked, value: "sms" },
    { label: "Contract", state: WorkflowStates.locked, value: "contract" },
  ];

  useEffect(() => {
    if (open) {
      setLoading(true);
      //#region setting the form to its initial state
      reset();
      setCurrentPGPView("form");
      setCurrentPFIView("form");
      setCurrentContractView("preparation");
      //#endregion setting the form to its initial state

      let promises: Promise<any>[] = [];

      //Get initial REMS scorings
      promises.push(
        getMemberTCARemsScores({
          member_id: member.id,
        }).then((res) => {
          if (res.success) {
            setUserIsSMSubscale(res.data.remsSubscaleSM >= TCA_REMS_SM_THRESHOLD);
            setUserIsSSSubscale(res.data.remsSubscaleSS >= TCA_REMS_SS_THRESHOLD);
            setUserIsATDSubscale(res.data.remsSubscaleATD >= TCA_REMS_ATD_THRESHOLD);
          }
        })
      );

      //Get previous PGPs
      promises.push(
        getMemberTCAGoals({
          member_id: member.id,
        })
          .then((results) => {
            const goalsCollection = results.data;
            const goalsCollectionKeys = Object.keys(goalsCollection);
            if (goalsCollectionKeys.length) {
              setValue("pgp", goalsCollection[goalsCollectionKeys[goalsCollectionKeys.length - 1]]);
              //prepare data for graph
              let prevGoals = [];
              Object.keys(goalsCollection).forEach((key, _i) => {
                goalsCollection[key].forEach((goal, i) => {
                  if (i + 1 > prevGoals.length) {
                    prevGoals.push({ goal: `Goal ${i + 1}` });
                  }
                  prevGoals[i][`Goal${i + 1}_${moment(key).format(DATE_FORMAT)}`] =
                    goal.ratingFarFromGoal;
                });
              });
              setPreviousGoals(prevGoals);
            }
          })
          .catch((e) => console.log(e))
      );

      //Get previous PFI
      promises.push(
        getMemberTCAPersonalFeelingsInventory({
          member_id: member.id,
        })
          .then((results) => {
            if (results.success) {
              const pfisCollection = results.data;
              pfisCollection.sort((a, b) =>
                a.datetime < b.datetime ? -1 : a.datetime > b.datetime ? 1 : 0
              );
              let prevPFIs = [{ pfi: "fatigue" }, { pfi: "energy" }, { pfi: "stress" }];
              pfisCollection.forEach((pfiDateEntry) => {
                prevPFIs.find((f) => f.pfi === "fatigue")[
                  "fatigue_" + moment(pfiDateEntry.datetime).format(DATE_FORMAT)
                ] = pfiDateEntry.fatigue_score;

                prevPFIs.find((f) => f.pfi === "energy")[
                  "energy_" + moment(pfiDateEntry.datetime).format(DATE_FORMAT)
                ] = pfiDateEntry.energy_score;

                prevPFIs.find((f) => f.pfi === "stress")[
                  "stress_" + moment(pfiDateEntry.datetime).format(DATE_FORMAT)
                ] = pfiDateEntry.stress_score;
              });
              setPreviousPFIs(prevPFIs);
            }
          })
          .catch((e) => console.log(e))
      );

      Promise.all(promises).then((_v) => {
        if (member.next_appointment_number === 1) {
          setTabs([...allFollowUpTabs.filter((f) => f.value !== "pfi")]);
        } else {
          setTabs([...allFollowUpTabs]);
        }
        setTabValue("pgp"); //When opening, open the first tab all the time
        setLoading(false);
      });
    }
  }, [open]);

  useEffect(() => {
    if (
      tabValue === "contract" &&
      ((currentContractView === "certificate" && member.next_appointment_number === 5) ||
        (currentContractView === "preview" && member.next_appointment_number !== 5))
    ) {
      setSubmitButtonLabel("Save");
    } else {
      setSubmitButtonLabel("Next");
    }
  }, [tabValue, currentContractView, member]);

  const setLoadingState = (state: boolean) => {
    setLoading(state);
  };

  //#region functions
  const handleProgressMemberInTCA = async (isFinalStep?: boolean) => {
    setLoading(true);

    // Final appointment
    if (isFinalStep) {
      //Add PGPs.
      const pgpPromise = new Promise(async function (resolve, reject) {
        const result = await addMemberTCAGoals({
          longTermGoals: getValues("pgp"),
          member_id: member.id,
          nextAppointmentDate: getValues("nextAppointmentDate"),
          setLoadingState: setLoadingState,
        });

        if (result["success"] === true) {
          resolve(`pgpPromise succeeded with result is ${JSON.stringify(result)}`);
        } else {
          reject(`pgpPromise failed with result is ${JSON.stringify(result)}`);
        }
      });
      await pgpPromise;

      // For the final appointment (graduation) we delete the users's TCA data except we keep the appointments (hist) and esp table data. This is so the user still shows in the reports.
      const deletePromise = new Promise(async function (resolve, reject) {
        const result = await deleteTCAMemberAppointments({
          full_delete: false,
          member_id: member.id,
          setLoadingState: setLoadingState,
        });

        if (result["success"] === true) {
          resolve(`deletePromise succeeded with result is ${JSON.stringify(result)}`);
        } else {
          reject(`deletePromise failed with result is ${JSON.stringify(result)}`);
        }
      });
      await deletePromise;
    } else {
      //Add PGPs
      const pgpPromise = new Promise(async function (resolve, reject) {
        const result = await addMemberTCAGoals({
          longTermGoals: getValues("pgp"),
          member_id: member.id,
          nextAppointmentDate: getValues("nextAppointmentDate"),
          setLoadingState: setLoadingState,
        });

        if (result["success"] === true) {
          resolve(`pgpPromise succeeded with result is ${JSON.stringify(result)}`);
        } else {
          reject(`pgpPromise failed with result is ${JSON.stringify(result)}`);
        }
      });
      await pgpPromise;

      if (member.next_appointment_number !== 1) {
        //Add PFI
        const pfiPromise = new Promise(async function (resolve, reject) {
          const result = await addMemberTCAPersonalFeelingsInventory({
            energy_score: getValues("energy_score"),
            fatigue_score: getValues("fatigue_score"),
            member_id: member.id,
            setLoadingState: setLoadingState,
            stress_score: getValues("stress_score"),
          });

          if (result["success"] === true) {
            resolve(`pfiPromise succeeded with result is ${JSON.stringify(result)}`);
          } else {
            reject(`pfiPromise failed with result is ${JSON.stringify(result)}`);
          }
        });
        await pfiPromise;
      }
    }

    setLoading(false);
    onClose(true);
  };
  //#endregion functions

  //#region event handlers
  const handleCloseModal = (isDiscardConfirmed) => {
    if (typeof isDiscardConfirmed === "boolean") {
      setShowDataWillBeLostModal(false);
      if (isDiscardConfirmed) {
        onClose();
      }
    } else {
      setShowDataWillBeLostModal(true);
    }
  };

  const handleCloseEmailModal = (inputValue, subject) => {
    if (inputValue) {
      const newEmail = inputValue !== contractEmail ? inputValue : false;
      sendContractToBackend(false, newEmail, subject);
    }
    setShowConfirmEmail(false);
  };

  const handleChangeTabByClickOnTab = (tabId) => {
    const destinationTab = tabs.find((f) => f.value === tabId);
    if (
      destinationTab.state === WorkflowStates.active ||
      destinationTab.state === WorkflowStates.complete
    ) {
      validateAndNavigateFromTab(tabId);
    }

    //Handling special case of contract sub tabs
    if (tabId !== "contract") setCurrentContractView("preparation");
  };

  const handleNextClick = () => {
    validateAndNavigateFromTab();
  };

  const validateAndNavigateFromTab = (toTab?: string) => {
    scrollToTop(".personal-details-modal-container");
    if (tabValue === "pgp") {
      if (currentPGPView === "form") {
        //Need to validate that we have at least 1 goal defined
        const pgpValidation = pgpRef.current.validate();
        if (pgpValidation > 0) {
          pgpRef.current.switchView("graph");
          setCurrentPGPView("graph");
        } else {
          if (pgpValidation === 0)
            toast("You need to specify at least 1 long term goal", { type: "error" });
        }
        return;
      } else {
        moveToTab(toTab);
        return;
      }
    }
    if (tabValue === "pfi") {
      if (currentPFIView === "form") {
        trigger().then((validationResult) => {
          if (!validationResult) {
            return;
          }
          pfiRef.current.switchView("graph");
          setCurrentPFIView("graph");
          return;
        });
        return;
      } else {
        moveToTab(toTab);
        return;
      }
    }
    if (tabValue === "contract") {
      if (currentContractView === "preparation") {
        trigger().then((validationResult) => {
          if (!validationResult) {
            return;
          }
          let pgpGoals = getValues("pgp");
          if (pgpGoals.filter((f) => f.isSelectedInContract).length === 0) {
            toast("You need to select at least 1 goal for the contract", { type: "error" });
            return;
          }
          if (toTab) {
            moveToTab(toTab);
          } else {
            setCurrentContractView("preview");
          }
          return;
        });
        return;
      }
      if (toTab) {
        moveToTab(toTab);
        return;
      }
      if (currentContractView === "preview" && member.next_appointment_number === 5) {
        setCurrentContractView("certificate");
        return;
      }
      if (currentContractView === "preview" && member.next_appointment_number !== 5 && !toTab) {
        handleProgressMemberInTCA();
      }
      if (currentContractView === "certificate") {
        handleProgressMemberInTCA(true);
      }
    }
    moveToTab(toTab);
  };

  const moveToTab = (toTab?: string) => {
    // if I have a toTab, go to it without updating completion
    if (toTab) {
      setTabValue(toTab);
    } else {
      let tempTabs = [...tabs];
      const currentTabIndex = tabs.findIndex((f) => f.value === tabValue);
      tempTabs[currentTabIndex].state = WorkflowStates.complete;

      if (currentTabIndex < tempTabs.length - 1) {
        tempTabs[currentTabIndex + 1].state = WorkflowStates.active;
        setTabValue(tabs[currentTabIndex + 1].value);
        setTabs([...tempTabs]);
      }
    }

    logMessage({
      member_id: member.id,
      message: `Got the next click. Current tab ${toTab}`,
    });
  };

  const handleBackClick = () => {
    if (tabValue === "pgp") {
      if (currentPGPView === "graph") {
        pgpRef.current.switchView("form");
        setCurrentPGPView("form");
        return;
      }
    }
    if (tabValue === "pfi") {
      if (currentPFIView === "graph") {
        pfiRef.current.switchView("form");
        setCurrentPFIView("form");
        return;
      }
    }
    if (tabValue === "contract") {
      if (currentContractView === "certificate") {
        setCurrentContractView("preview");
        return;
      }
      if (currentContractView === "preview") {
        setCurrentContractView("preparation");
        return;
      }
    }

    const currentTabIndex = tabs.findIndex((f) => f.value === tabValue);

    if (currentTabIndex !== 0) {
      setTabValue(tabs[currentTabIndex - 1].value);
    }
  };
  //#endregion event handlers

  const sendContractToBackend = async (preview = true, newEmail = "", subject = "") => {
    const content = document.getElementsByClassName("elementToPrint")[0] as HTMLElement;
    const contentContract = document.getElementsByClassName("contractTitle")[0];

    if (contentContract) {
      setEmailSubject(
        `The Coach Approach Contract for Appointment #${member.next_appointment_number}`
      );
    } else {
      setEmailSubject(
        `The Coach Approach Materials for Appointment #${member.next_appointment_number}`
      );
    }

    const originalDisplay = content.style.display;
    const originalPadding = content.style.padding;

    content.style.display = "block";
    content.style.padding = "20mm";

    const options = {
      filename: `test.pdf`,
      html2canvas: { logging: true, scale: 1, useCORS: true },
      image: { quality: 0.98, type: "jpeg" },
      jsPDF: { format: "A4", orientation: "portrait" },
      margins: 1,
    };

    const blob = preview
      ? null
      : await html2pdf()
          .set(options)
          .from(content)
          .toPdf()
          .output("blob")
          .then((data: Blob) => {
            return data;
          });

    content.style.display = originalDisplay;
    content.style.padding = originalPadding;

    toast(DOCUMENT_BEING_PROCESSED, { type: "info" });

    try {
      const email = newEmail ? newEmail : contractEmail;

      if (!preview) {
        const isValid = await validateEmail(email.toLowerCase());
        if (!isValid) {
          return setShowConfirmEmail(true);
        }
      }
      const response = await emailContract(member.id, blob, preview, email, subject);
      if (preview) {
        setContractEmail(response?.payload?.strEmailAddress);
        setShowConfirmEmail(true);
      } else {
        toast(DOCUMENT_SENT_SUCCESSFUL, { type: "success" });
      }
    } catch (e) {
      console.log("e", e);
      toast(DOCUMENT_SENT_FAILED, { type: "error" });
    }
  };

  return (
    <>
      <Modal
        closeAfterTransition
        BackdropProps={{
          timeout: 500,
        }}
        open={open}
        onClose={handleCloseModal}
      >
        <Stack className="personal-details-modal-container">
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            marginBottom="40px"
            className="header"
          >
            <Box>
              <IconYMCATCALogo />
            </Box>
            <DeleteIcon width="24px" height="24px" onClick={() => handleCloseModal(null)} />
          </Stack>
          {loading && <ScreenLoader isScreenLoader />}
          {!loading && (
            <>
              <Box mb={2}>
                <TCAUserSummary
                  member={member}
                  appointment={`Follow Up ${member?.next_appointment_number} of 5`}
                  isSMSubscale={userIsSMSubscale}
                  isSSSubscale={userIsSSSubscale}
                  isATDSubscale={userIsATDSubscale}
                />
              </Box>
              <Tabs
                style={{ marginBottom: 30 }}
                value={tabValue}
                onChange={handleChangeTabByClickOnTab}
                tabs={tabs}
              />
              <FormProvider {...methods}>
                <Box className="followupTCASteps">
                  <form>
                    <Stack height={"100%"}>
                      <PGP
                        currentTab={tabValue}
                        previousGoals={previousGoals}
                        goals={watch("pgp")}
                        appointmentNumber={member?.next_appointment_number}
                        ref={pgpRef}
                        shareSubView={(v) => setCurrentPGPView(v ? `graph` : `form`)}
                      />
                      <PFI
                        previousPFIs={previousPFIs}
                        currentTab={tabValue}
                        ref={pfiRef}
                        shareSubView={(v) => setCurrentPFIView(v ? `graph` : `form`)}
                      />
                      <SMS
                        currentTab={tabValue}
                        appointmentNumber={member?.next_appointment_number}
                        sendContractToBackend={sendContractToBackend}
                      />
                      <TCAContract
                        goals={watch("pgp")}
                        contractWorkoutSessions={watch("contract.workoutSessions")}
                        contractNumberOfWeeks={watch("contract.numberOfWeeks")}
                        currentTab={tabValue}
                        member={member}
                        view={currentContractView}
                        appointmentNumber={member?.next_appointment_number}
                        sendContractToBackend={sendContractToBackend}
                      />
                    </Stack>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="flex-end"
                      marginTop="30px"
                    >
                      <Stack direction="row" alignItems="center" spacing={3} flexGrow={1}>
                        <Box flexGrow={1}>
                          <Button
                            label="Back"
                            type="button"
                            className="text-button"
                            onClick={() => handleBackClick()}
                          />
                        </Box>
                        <Button
                          label="Cancel"
                          type="button"
                          className="text-button"
                          onClick={handleCloseModal}
                        />
                        <Button
                          label={submitButtonLabel}
                          type="button"
                          className="confirm-button"
                          onClick={handleNextClick}
                        />
                      </Stack>
                    </Stack>
                  </form>
                </Box>
              </FormProvider>
            </>
          )}
        </Stack>
      </Modal>
      <ConfirmationBox
        open={showDataWillBeLostModal}
        onClose={handleCloseModal}
        title={"Discard Changes?"}
        message={<>If answered Yes, all information filled will be lost.</>}
        type={"confirmation"}
      />
      <ConfirmationBox
        open={showConfirmEmail}
        onClose={handleCloseEmailModal}
        mainTitle={"Send Document"}
        title={"Sending Coach Approach Document"}
        message={<>Please verify that the member's email address is valid</>}
        type={"confirmation"}
        initialInputValue={contractEmail}
        yesButtonText="Send"
        noButtonText="Cancel"
        initialInputValueAlt={emailSubject}
      />
    </>
  );
};

export default FollowUpTCASteps;
